import React, { useState, useEffect } from "react";
import "./messages.scss";
import PlantHeader from "../plantHeader/plantHeader";
import Menu from "../menu/menu";
import Search from "../search/search";
import addIcon from "./add-icon.svg";
import EditMessage from "../editMessage/editMessage";
import Loader from "../loader/loader";
import Modal from "../modal/modal";
import { TimeZone } from "../utils";
import { useNavigate } from "react-router-dom";
import { LOCAL_STORAGE_PROJECT, LOCAL_STORAGE_USER_KEY } from "../../consts";
import ApiHandler from "../api";
import sortIcon from "../robotSettingsNew/sort.png";
import sortUpIcon from "../robotSettingsNew/sort-up.png";
import sortDownIcon from "../robotSettingsNew/sort-down.png";
import {
  getCurrentProject,
  redirectIfNoProject,
} from "../../utils/projectUtils";

const MessagesComponent = () => {
  const navigate = useNavigate();
  const apiHandler = new ApiHandler();

  // State management
  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditMessage, setShowEditMessage] = useState(false);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortParam, setSortParam] = useState("");
  const [sortDirection, setSortDirection] = useState(1);
  const [timeZone, setTimeZone] = useState(null);

  // Initialize project and user from localStorage/URL
  useEffect(() => {
    // Check if we need to redirect due to missing project
    if (redirectIfNoProject(navigate)) {
      return;
    }

    const currentProject = getCurrentProject();
    const storedUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_KEY));

    if (!currentProject) {
      navigate("/projects");
      return;
    }

    setProject(currentProject);
    setUser(storedUser);
    setTimeZone(new TimeZone(currentProject));
  }, []);

  // Load messages when project is available
  useEffect(() => {
    if (project?._id) {
      loadMessages();
    }
  }, [project, search]);

  // Load messages from API
  const loadMessages = async () => {
    if (!project?._id) return;

    setIsLoading(true);
    try {
      const result = await apiHandler.getMessages(
        project._id,
        search,
        null,
        startDate,
        endDate
      );
      setMessages(result.messages);
    } catch (error) {
      console.error("Error loading messages:", error);
      alert("Failed to load messages");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle message selection
  const messageSelected = (message) => {
    setSelectedMessage({
      ...message,
      dueDate: new Date(message.dueDate),
      created: new Date(message.created),
    });
    setShowEditMessage(true);
  };

  // Close edit message modal
  const closeEditMessage = (show = false) => {
    setShowEditMessage(show);
    if (!show) {
      loadMessages(); // Refresh messages after editing
    }
  };

  // Handle search
  const searchClicked = (searchTerm) => {
    setSearch(searchTerm);
  };

  // Create new message
  const createNewMessage = () => {
    if (!project || !project._id) return;

    const newMessageObj = {
      projectId: project._id,
      created: Date.now(),
      expiryDate: Date.now(),
      description: "",
      level: 1,
    };

    setSelectedMessage(newMessageObj);
    setShowEditMessage(true);
  };

  // Close delete confirmation modal
  const closeDeleteMessageModal = () => {
    setShowDeleteMessage(false);
    setShowEditMessage(true);
  };

  // Delete message
  const deleteMessage = async () => {
    if (!selectedMessage?._id || !project?._id) return;

    setShowDeleteMessage(false);
    setShowEditMessage(false);
    setIsLoading(true);

    try {
      await apiHandler.deleteMessage(selectedMessage._id, project._id);
      await loadMessages(); // Refresh messages after deletion
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message");
    } finally {
      setIsLoading(false);
    }
  };

  // Get header TH with sorting
  const getHeaderTH = (paramName, text, className) => {
    return <th className={className}>{text}</th>;
  };

  if (!project) {
    return null;
  }

  return (
    <div className="page">
      {isLoading ? <Loader /> : null}

      {showEditMessage ? (
        <EditMessage
          closeEditMessage={() => {
            closeEditMessage(false);
          }}
          message={selectedMessage}
          deleteMessage={() => {
            setShowDeleteMessage(true);
            setShowEditMessage(false);
          }}
        />
      ) : null}

      {showDeleteMessage ? (
        <Modal title={`Delete this message?`} onClose={closeDeleteMessageModal}>
          <div className="buttons-wrapper">
            <div className="button cancel" onClick={closeDeleteMessageModal}>
              Cancel
            </div>
            <div className="button" onClick={deleteMessage}>
              Delete forever
            </div>
          </div>
        </Modal>
      ) : null}

      <div className="menu-wrapper">
        <Menu />
      </div>
      <div className="content">
        <PlantHeader title={"Messages"} secondaryText={project.projectName} />
        <div className="messages-table">
          <div className="top">
            <Search onSearch={searchClicked} placeholder="search messages" />

            {user?.role !== "projectOwner" ? (
              <div className="download" onClick={createNewMessage}>
                <img src={addIcon} />
                NEW MESSAGE
              </div>
            ) : null}
          </div>
          <table>
            <tbody>
              <tr>
                {getHeaderTH("created", "Created")}
                {project.arrayType === "solar-tracking"
                  ? getHeaderTH("vehicle.vehicleNumber", "Vehicle Number")
                  : null}
                {getHeaderTH("description", "Description")}
                {getHeaderTH("expiryDate", "Expiry Date")}
                {getHeaderTH("level", "Level")}
              </tr>
              {messages.map((message, index) => {
                return (
                  <tr key={index} onClick={() => messageSelected(message)}>
                    <td className="date">
                      {message.createdBy?.fullName}
                      <br />
                      {timeZone?.getLocalTime(message.created, false, true)}
                    </td>
                    {project.arrayType === "solar-tracking" ? (
                      <td>
                        {message.vehicleId
                          ? message.vehicleId.vehicleNumber
                          : ""}
                      </td>
                    ) : null}
                    <td>{message.description}</td>
                    <td>
                      {timeZone?.getLocalTime(message.expiryDate, false, true)}
                    </td>

                    <td>{message.level}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MessagesComponent;
